<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { formatDistanceToNow, parseISO, isValid } from 'date-fns'
import { Bell, Volume2, VolumeX } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import {
  AppSwitch,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/shared'
import { useMessagingNotificationsStore } from '@/shared/stores/messagingNotifications'
import { buildUcUserMessagingShowRoute } from '@/modules/uc-user/messaging/constants'

withDefaults(
  defineProps<{
    /** Slightly denser trigger for mobile header */
    compact?: boolean
  }>(),
  { compact: false }
)

const router = useRouter()
const store = useMessagingNotificationsStore()
const { items, unreadCount, soundEnabled, loading } = storeToRefs(store)

const badgeLabel = computed(() => {
  const n = unreadCount.value
  if (n <= 0) return ''
  return n > 99 ? '99+' : String(n)
})

function formatWhen(iso: string): string {
  try {
    const date = parseISO(iso)
    if (!isValid(date)) return ''
    return formatDistanceToNow(date, { addSuffix: true })
  } catch {
    return ''
  }
}

const onOpenItem = (threadId: string, phoneNumberId: string, accountId: string | null) => {
  store.dismissThread(threadId)
  void router.push(
    buildUcUserMessagingShowRoute(phoneNumberId, accountId, undefined, 'messages', threadId)
  )
}

const onToggleSound = (value: boolean) => {
  store.setSoundEnabled(value)
}
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <button
        type="button"
        class="relative inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus:ring-2 focus:ring-ring"
        aria-label="Notifications"
        data-testid="messaging-notifications-bell"
      >
        <Bell class="h-4 w-4" />
        <span
          v-if="badgeLabel"
          class="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold leading-none text-destructive-foreground"
        >
          {{ badgeLabel }}
        </span>
      </button>
    </DropdownMenuTrigger>

    <DropdownMenuContent align="end" class="w-[22rem] p-0" :side-offset="8">
      <div class="flex items-center justify-between px-3 py-2.5">
        <DropdownMenuLabel class="p-0 text-sm font-semibold">Notifications</DropdownMenuLabel>
        <span v-if="loading" class="text-[11px] text-muted-foreground">Updating…</span>
      </div>
      <DropdownMenuSeparator class="m-0" />

      <div class="max-h-80 overflow-y-auto">
        <button
          v-for="item in items"
          :key="item.id"
          type="button"
          class="flex w-full flex-col gap-0.5 border-b border-border/60 px-3 py-2.5 text-left transition-colors hover:bg-accent/60 last:border-b-0"
          @click="onOpenItem(item.threadId, item.phoneNumberId, item.accountId)"
        >
          <div class="flex items-start justify-between gap-2">
            <span class="truncate text-[13px] font-semibold text-foreground">{{ item.title }}</span>
            <span class="shrink-0 text-[10px] text-muted-foreground">
              {{ formatWhen(item.occurredAt) }}
            </span>
          </div>
          <p class="line-clamp-2 text-[12px] text-muted-foreground">{{ item.preview }}</p>
          <span
            v-if="item.isNew"
            class="mt-0.5 w-fit rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary"
          >
            New
          </span>
        </button>

        <div v-if="items.length === 0" class="px-3 py-8 text-center text-sm text-muted-foreground">
          No unread messages
        </div>
      </div>

      <DropdownMenuSeparator class="m-0" />
      <div class="flex items-center justify-between gap-3 px-3 py-2.5">
        <div class="flex items-center gap-2 text-xs text-muted-foreground">
          <Volume2 v-if="soundEnabled" class="h-3.5 w-3.5" />
          <VolumeX v-else class="h-3.5 w-3.5" />
          <span>Notification sound</span>
        </div>
        <AppSwitch
          size="sm"
          :model-value="soundEnabled"
          data-testid="messaging-notifications-sound-toggle"
          @update:model-value="onToggleSound"
        />
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
